import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormRecord, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormGroupService } from 'src/app/components/form-group/form-group.service';
import { TabItem } from 'src/app/components/form-group/form-group.types';

@Component({
  selector: 'app-tab-item-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule
  ],
  templateUrl: './form.component.html',
  styleUrl: './form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormComponent {
  readonly #destroyRef = inject(DestroyRef);
  readonly #formGroupService = inject(FormGroupService);
  readonly #cd = inject(ChangeDetectorRef);

  @Input({ required: true }) element!: Record<string, string>;
  @Input({ required: true }) key!: string;
  @Input({ required: false }) options: { id: string, name: string; }[] = [];

  readonly item = input.required<TabItem>();
  readonly isRequired = signal(false);
  readonly touched = signal(false);

  form!: FormRecord<FormControl>;

  constructor() {
    effect(() => {
      const item = this.item();
      this.#buildForm();
    });

  }

  get upperKey(): string {
    return this.item().key[0].toUpperCase() + this.item().key.substring(1);
  }

  #buildForm() {
    switch (this.item().type) {
      case 'input':
      case 'textarea':
      case 'number':
      case 'tel':
      case 'select':
      case 'datepicker':
        this.form = new FormRecord({
          [this.item().key]: new FormControl(this.element[this.item().key], { nonNullable: !!this.element[this.item().key], validators: Validators.required })
        });
        break;
      case 'selectWithOther':
        this.form = new FormRecord({
          [this.item().key]: new FormControl(this.element[this.item().key], { nonNullable: !!this.element[this.item().key] }),
          [`${this.item().key}Other`]: new FormControl(this.element[`${this.item().key}Other`], { nonNullable: !!this.element[`${this.item().key}Other`] })
        });
        break;
      case 'daterangepicker':
        this.form = new FormRecord({
          [`${this.item().key}StartDate`]: new FormControl(this.element[`${this.item().key}StartDate`], { nonNullable: !!this.element[`${this.item().key}StartDate`] }),
          [`${this.item().key}EndDate`]: new FormControl(this.element[`${this.item().key}EndDate`], { nonNullable: !!this.element[`${this.item().key}EndDate`] })
        });
        break;
      case 'datetime':
        this.form = new FormRecord({
          [`${this.item().key}Date`]: new FormControl(this.element[`${this.item().key}Date`], { nonNullable: !!this.element[`${this.item().key}Date`] }),
          [`${this.item().key}Time`]: new FormControl(this.element[`${this.item().key}Time`], { nonNullable: !!this.element[`${this.item().key}Time`] })
        });
        break;
      case 'addressEnglishDisplay':
        this.form = new FormRecord({
          [`${this.item().key}Postcode`]: new FormControl(this.element[`${this.item().key}Postcode`], { nonNullable: !!this.element[`${this.item().key}Postcode`] }),
          [`${this.item().key}State`]: new FormControl(this.element[`${this.item().key}State`], { nonNullable: !!this.element[`${this.item().key}State`] }),
          [`${this.item().key}Address1`]: new FormControl(this.element[`${this.item().key}Address1`], { nonNullable: !!this.element[`${this.item().key}Address1`] }),
          [`${this.item().key}Address2`]: new FormControl(this.element[`${this.item().key}Address2`], { nonNullable: !!this.element[`${this.item().key}Address2`] }),
        });
        break;
      case 'name':
        this.form = new FormRecord({
          [`last${this.upperKey}`]: new FormControl(this.element[`last${this.upperKey}`], { nonNullable: !!this.element[`last${this.upperKey}`], validators: Validators.required }),
          [`first${this.upperKey}`]: new FormControl(this.element[`first${this.upperKey}`], { nonNullable: !!this.element[`first${this.upperKey}`], validators: Validators.required })
        });
        break;
      case 'address':
        this.form = new FormRecord({
          postcode: new FormControl(this.element['postcode'], { nonNullable: !!this.element['postcode'] }),
          prefecture: new FormControl(this.element['prefecture'], { nonNullable: !!this.element['prefecture'] }),
          city: new FormControl(this.element['city'], { nonNullable: !!this.element['city'] }),
          address1: new FormControl(this.element['address1'], { nonNullable: !!this.element['address1'] }),
          address2: new FormControl(this.element['address2'], { nonNullable: !!this.element['address2'] })
        });
        break;
      case 'addressEnglish':
        this.form = new FormRecord({
          prefectureEnglish: new FormControl(this.element['prefectureEnglish'], { nonNullable: !!this.element['prefectureEnglish'] }),
          cityEnglish: new FormControl(this.element['cityEnglish'], { nonNullable: !!this.element['cityEnglish'] }),
          address1English: new FormControl(this.element['address1English'], { nonNullable: !!this.element['address1English'] }),
          address2English: new FormControl(this.element['address2English'], { nonNullable: !!this.element['address2English'] })
        });
        break;
      case 'term':
      case 'pdf':
      case 'image':
      case 'document':
      case 'supportStatus':
      case 'supportStatusWithLimit':
      case 'age':
      case 'country':
      case 'course':
      case 'plan':
      case 'lesson':
        throw '';
    }
    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(value => {
        Object.assign(this.element, value);
      });

    for (const key of Object.keys(this.form.getRawValue())) {
      this.#formGroupService.keys.push(key);
    }

    this.#formGroupService.touched$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.touched.set(true);
      })
      .add(() => this.#cd.markForCheck());

    this.#cd.markForCheck();
  }

  getValue(): any {
    return this.form?.getRawValue();
  }
}
